import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Dialog, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { ImportService } from "../../../../Component/Api/Import/ImportService";
import { formControlColour, tooltipColour } from "../../../../Component/Common/Components/BEE123.Constants";
import AppDropzone from "../../../../Component/Common/Components/FormValidationInputs/AppDropZone";
import AppSelectList from "../../../../Component/Common/Components/FormValidationInputs/AppSelectList";
import { MDAlert } from "../../../../Component/Common/Components/MDAlert";
import { MDBox } from "../../../../Component/Common/Components/MDBox";
import { MDTypography } from "../../../../Component/Common/Components/MDTypography";
import { ImportProgressBarWithErrorList } from "../../../../Component/Common/Components/ProgressBar/ImportProgressBarWithErrors";
import { TooltipLoadingButton } from "../../../../Component/Common/Components/Tooltip/TooltipLoadingButton";
import { ExportKeyValue } from "../../../../Component/Model/Dto/Upload";
import { DataImportType } from "../../../../Component/Model/Enum/BEE123Enums";
import { ImportCommonFunctionsDropDown, getUploadFileName } from "../../../../Helpers/ImportHelper";
import { defaultObject, emptyGuid } from "../../../../Helpers/typescriptHelper";
import { UserAtom, userRolesState } from "../../../../State/user.Atom";
import { PlatformSalaryImportTemplateModal } from "./PlatformSalaryImportTemplateModal";
import { PlatformPeopleSalaryEditRole, UserAdminRole } from "../../../../Component/Common/Constants/UserRoles";
import { CoreImportService } from "../../../../Component/Api/Import/CoreImportService";
import { VerificationWorkflowService } from "../../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService";
import {useSignalRConnection} from "../../../../Hooks/useSignalRConnection";

interface IProps {
    open: boolean;
    closeModal: () => void;
    scorecardId?: string;
    organisationId: number;
}

export interface ProgressData {
    progress: number;
    numberOfRowsCorrect: number;
    numberOfRowsFailed: number;
    log: any[];
    isSuccess: boolean;
}

export const PlatformImportModal = ({ open, closeModal, scorecardId, organisationId }: IProps) => {
    const user = useRecoilValue(UserAtom);
    const userRoles = useRecoilValue(userRolesState);
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [progressData, setProgressData] = useState<ProgressData>({
        progress: 0,
        numberOfRowsCorrect: 0,
        numberOfRowsFailed: 0,
        log: [],
        isSuccess: false,
    });

    const [dataImportType, setDataImportType] = useState<DataImportType>(DataImportType.PlatformPeople);
    const [showPlatformSalaryImportTemplateModal, setShowPlatformSalaryImportTemplateModal] = useState(false);
    const [importManagementTrackerId, setImportManagementTrackerId] = useState<number>(0);
    const hub = useSignalRConnection("ImportHub");

    const { control, handleSubmit, watch, setValue } = useForm({
        mode: 'all',
    });

    const importRolesAccess = [
        PlatformPeopleSalaryEditRole,
        UserAdminRole
    ]
    const canImportSalaries = userRoles.some(r => importRolesAccess.includes(r));

    const clearFile = () => {
        setValue('file', null);
    };

    useEffect(() => {
        if (importManagementTrackerId === 0) return;

        (async () => {
            try {
                if(!hub.isConnected)
                {
                    await hub.start();
                }

                hub.on(`excelImportProgress-${organisationId}-${importManagementTrackerId}`, (data: any) => {
                    setProgressData(data);
                })

                hub.on(`excelImportFailed-${organisationId}-${importManagementTrackerId}`, (data: any) => {
                    const { NumberOfRowsCorrect, NumberOfRowsFailed, Log } = data;
                    setProgressData({
                        ...data,
                        progress: 0,
                        isSuccess: false,
                        numberOfRowsCorrect: NumberOfRowsCorrect,
                        numberOfRowsFailed: NumberOfRowsFailed,
                        log: Log
                    });
                    toast.error("Import Failed");
                    setIsImporting(false);
                });

                hub.on(`excelImportSuccess-${organisationId}-${importManagementTrackerId}`, (data: any) => {
                    const { NumberOfRowsCorrect, Log } = data;
                    setProgressData({
                        ...data,
                        isSuccess: true,
                        progress: 100,
                        numberOfRowsCorrect: NumberOfRowsCorrect,
                        log: Log
                    });
                    toast.success("Import Succeeded");
                    setIsImporting(false);
                })
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();
    }, [importManagementTrackerId]);

    const getPlatformPeopleImportTemplate = (e: any) => {
        ImportService.DownloadImportTemplate(emptyGuid, getUploadFileName(DataImportType[DataImportType.PlatformPeople]));
    };

    const getPlatformSalaryImportTemplate = (e: any) => {
        setShowPlatformSalaryImportTemplateModal(true);
    }

    const checkForInProgressImport = async (importType: number): Promise<boolean> => {
        try {
            const result = await VerificationWorkflowService.IsImportInProgressForOrganisation(organisationId, importType);
            return result.data;
        } catch (error) {
            console.log(error);
            toast.error("An error has occurred");
            return false;
        }
    };

    const onDataSubmit = async (data: FieldValues) => {
        setProgressData(defaultObject);
        setIsImporting(true);
        try {
            const parameters: ExportKeyValue[] = [];

            const isInProgress = await checkForInProgressImport(dataImportType);
            if (isInProgress){
                toast.error(`An import is already in progress. Please try again later`)
                setIsImporting(false); 
                return; 
            }

            const request = {
                dataImportTypes: [dataImportType],
                dataImportType: dataImportType,
                importAction: parseInt(data.importType),
                scorecardId: null,
                file: data.file,
                organisationId: organisationId,
                parameters: parameters
            }
            var result = await CoreImportService.ImportMultiPageExcelFile(request);

            if (result.data.wasSuccessful) {
                setImportManagementTrackerId(result.data.importManagementTrackerId);
            }
            else {
                toast.error("Import Failed");
                setIsImporting(false);
                setProgressData({
                    ...data,
                    progress: 100,
                    isSuccess: false,
                    numberOfRowsCorrect: result.data.numberOfRowsCorrect,
                    numberOfRowsFailed: result.data.numberOfRowsFailed,
                    log: result.data.log
                });
            }
        } catch (error) {
            console.log(error);
            setIsImporting(false);
            toast.error('Import Failed');
        }
    }

    const file = watch('file');
    const importType = watch('importType');

    const getTooltipTitle = () => {
        if (!file && !importType) {
            return "File and Import Type is required"
        }
        else if (!file) {
            return "File is required";
        } else if (!importType) {
            return "Import Type is required";
        } else {
            return "";
        }
    }

    return (
        <>
            <Dialog open={open} onClose={() => closeModal()} maxWidth={false} aria-labelledby="form-dialog-title">
                <form
                    id="ImportForm"
                    onSubmit={handleSubmit(onDataSubmit)}
                >
                    <DialogTitle>
                        File Upload
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={closeModal}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent sx={{ paddingTop: "5px !important", width: "700px" }}>
                        <FormControl fullWidth sx={{ marginBottom: '10px' }}>
                            <InputLabel id="select-data-import-type-label" disabled={isImporting}>Select what you are uploading</InputLabel>
                            <Select
                                label="Select what you are uploading"
                                disabled={isImporting}
                                sx={{
                                    height: 40,
                                }}
                                labelId="select-data-import-type-label"
                                value={dataImportType}
                                onChange={(event) => {
                                    const dataImportTypeSelected = event.target.value as DataImportType;
                                    setDataImportType(dataImportTypeSelected);
                                }}
                            >
                                <MenuItem key={DataImportType.PlatformPeople} value={DataImportType.PlatformPeople}>
                                    People
                                </MenuItem>
                                {canImportSalaries &&
                                    <MenuItem key={DataImportType.PlatformPeople} value={DataImportType.PlatformSalary}>
                                        Salaries
                                    </MenuItem>
                                }
                            </Select>
                        </FormControl>

                        <AppSelectList
                            disabled={isImporting}
                            control={control}
                            name="importType"
                            label="Import Type"
                            items={ImportCommonFunctionsDropDown(DataImportType[dataImportType])}
                        />
                        <Box mt={2}>
                            {!file && <AppDropzone control={control} name="file" width={"100%"} isExcel></AppDropzone>}
                            {file && (
                                <Box display="flex" flexDirection="column" alignItems="center">
                                    <Box display="flex" justifyContent="flex-end" alignItems="flex-start">
                                        <Box position="relative">
                                            <img src={`${process.env.PUBLIC_URL}/img/xlsx-icon.png`} alt='preview' style={{ maxHeight: '200px' }} />
                                            <IconButton
                                                disabled={isImporting}
                                                onClick={clearFile}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    zIndex: 1
                                                }}
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <MDTypography variant="caption" align="center">{file.name}</MDTypography>
                                </Box>
                            )}
                        </Box>
                        <Box mt={2}>
                            <ImportProgressBarWithErrorList
                                value={progressData?.progress ?? 0}
                                progressData={progressData}
                            />
                        </Box>
                        <Grid item xs={5}>
                            <TooltipLoadingButton
                                buttonText="Begin Import"
                                form="ImportForm"
                                tooltipTitle={getTooltipTitle()}
                                disabled={!file || !importType}
                                isLoading={isImporting}
                                showTooltipOnDisabled={true} />
                        </Grid>
                        <Grid item xs={12} lg={12}>
                            <MDBox mt={4} color="white">
                                <MDAlert color='light'>
                                    <MDBox>
                                        <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>
                                            <p style={{ marginBottom: "0.2rem" }}> Click<Button variant="text" size="small" onClick={getPlatformPeopleImportTemplate} style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                                                to download the people template file</p>
                                        </MDTypography>
                                        {canImportSalaries &&
                                            <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular' >
                                                <p style={{ marginBottom: "0.2rem", alignItems: 'center' }}> Click<Button variant="text" size="small" onClick={getPlatformSalaryImportTemplate} style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                                                    to download the salary template file
                                                    <Tooltip
                                                        title={"Importing people is required before importing salary information."}
                                                        arrow
                                                        componentsProps={{
                                                            tooltip: {
                                                                sx: {
                                                                    bgcolor: tooltipColour,
                                                                    '& .MuiTooltip-arrow': {
                                                                        color: tooltipColour
                                                                    },
                                                                },
                                                            },
                                                        }}>
                                                        <IconButton
                                                            size="small"
                                                            edge="end"
                                                            color="inherit"
                                                            aria-label="info"
                                                        >
                                                            <InfoOutlinedIcon style={{ color: formControlColour }} />
                                                        </IconButton >
                                                    </Tooltip></p>
                                            </MDTypography>
                                        }
                                    </MDBox>
                                </MDAlert>
                            </MDBox>
                        </Grid>
                    </DialogContent>
                </form>
            </Dialog>

            {showPlatformSalaryImportTemplateModal &&
                <PlatformSalaryImportTemplateModal
                    open={showPlatformSalaryImportTemplateModal}
                    closeModal={() => setShowPlatformSalaryImportTemplateModal(false)}
                />
            }
        </>
    )
}